<template>
  <div class="invite-contact-modal">
    <p class="title">
      {{ $t('handover.arrangeNotify') }}
    </p>
    <div :class="['actual-handover-date', {'actual-handover-date-error': scheduledTransactionDateError}]">
      <span class="title">{{ $t('handover.scheduledTransactionDateAndTime') }}</span>
      <date-time-selector
        :timeOptions="options"
        :date="scheduledTransactionDate"
        :setMinTime="true"
        :disabledDates="{to: yesterday}"
        @updateDate="val => updateScheduledTransactionDate(val)"
      />
      <error-tip
        v-if="scheduledTransactionDateError"
        :extendClass="datePickErr"
        :context="$t('hint.inputTip.string')"
      />
    </div>
    <div class="contact-info hkld-contact">
      <div class="main-contact info">
        <span class="title">{{ $t('handover.hklContactName') }}</span>
        <div
          v-for="(user, index) in selectedUser"
          :key="index"
          class="multi-content"
        >
          <HKLSelect
            class="hkl-select_search-input-flush"
            :class="{'error-highlight': user.emptyUser}"
            :value="user.id || undefined"
            :options="getHkldUserHklOptions(index)"
            :showSearch="true"
            placeholder=""
            @change="val => updateSelect(val, index)"
          />
          <i
            :class="['fa fa-lg', index ? 'fa-times' : 'fa-plus' ]"
            @click="index ? deleteSelectedUser(index) : addEditabelMultiSelect()"
          />
        </div>
      </div>
    </div>
    <div class="contact-info">
      <div class="line">
        <div class="main-contact info">
          <span class="title">{{ $t('handover.handoverContactName') }}</span>
          <div class="content">
            <template v-for="fullnameObj of getFullnameSorts">
              <InputField
                :key="`${fullnameObj.key}.input`"
                :extendClass="contactInfo[fullnameObj.value].error ? 'error-highlight' : ''"
                :showColon="false"
                :showFlag="false"
                :placeholder="$t(`contact.basic.${fullnameObj.label}`)"
                :value="contactInfo[fullnameObj.value].value"
                @updateValue="val => updateItemValue(fullnameObj.value, val)"
              />
              <error-tip
                v-if="contactInfo[fullnameObj.value].error"
                :key="`${fullnameObj.key}.error`"
                :extendClass="`${fullnameObj.value}Err`"
                :showTip="true"
                :context="$t('hint.inputTip.name')"
              />
            </template>
          </div>
        </div>
        <div class="phone-number info">
          <span class="title">{{ $t('handover.handoverContactPhoneNumber') }}</span>
          <InputField
            :extendClass="contactInfo.phoneNumber.error ? 'error-highlight' : ''"
            :showColon="false"
            :showFlag="false"
            :value="contactInfo.phoneNumber.value"
            @updateValue="val => updateItemValue('phoneNumber', val)"
          />
          <error-tip
            v-if="contactInfo.phoneNumber.error"
            :showTip="true"
            :context="$t('hint.inputTip.phone')"
          />
        </div>
      </div>
      <div class="email info">
        <span class="title">{{ $t('handover.handoverContactEmail') }}</span>
        <InputField
          class="email"
          :extendClass="contactInfo.email.error ? 'error-highlight' : ''"
          :showColon="false"
          :showFlag="false"
          :value="contactInfo.email.value"
          @updateValue="val => updateItemValue('email', val)"
        />
        <error-tip
          v-if="contactInfo.email.error"
          :showTip="true"
          :context="$t('hint.inputTip.email')"
        />
      </div>
      <div class="email info">
        <span class="title">{{ $t('cc') }}</span>
        <TextField
          :extendClass="contactInfo.additionalEmails.error?'error-highlight':''"
          :value="contactInfo.additionalEmails.value"
          @updateValue="val => updateItemValue('additionalEmails', val)"
        />
        <error-tip
          v-if="contactInfo.additionalEmails.error"
          :showTip="true"
          :context="$t('hint.inputTip.email')"
        />
      </div>
      <div class="remark info">
        <span class="title">{{ $t('handover.remarkToTenant') }}</span>
        <TextField
          :showColon="false"
          :showFlag="false"
          :value="remarkToTenant"
          placeholder=""
          @updateValue="val => updateRemark(val)"
        />
      </div>
    </div>
    <div class="group-btns">
      <div
        class="confirm btn--orange"
        @click="confirm"
      >
        {{ $t('handover.arrangeNotify') }}
      </div>
      <div
        class="cancel btn--darkblue"
        @click="cancel"
      >
        {{ $t('action.cancel') }}
      </div>
    </div>
  </div>
</template>

<script>
import lodash from 'lodash'
import moment from 'moment'
import { mapActions, mapMutations, mapGetters } from 'vuex'
import InputField from '@/controls/InputField'
import TextField from '@/controls/TextField'
import { hotbStatus, hotbAttrTypes } from '@/utils/locale'
import mutationTypes from '@/store/mutation-types'
import { contactTypes } from '@/api/users/consts'
import { ARRANGE_ATTRS } from '@/api/hotb/consts'
import * as regex from '@/utils/regex'
import formFullnameMixin from '@/pages/formFullnameMixin'
import DateTimeSelector from '@/controls/DateTimeSelector'
import HKLSelect from '@/controls/Select/HKLSelect.vue'
import dateUtil from '@/utils/dateUtil'
import { sendEmail } from '@/api/postman/request'
import { EmailTypes, ActionTypes } from '@/utils/consts'
import { splitEmails } from '@/api/postman/util'
import ErrorTip from './ErrorTip'
export default {
  name: 'InviteContactModal',
  components: {
    InputField,
    TextField,
    ErrorTip,
    HKLSelect,
    DateTimeSelector,
  },
  mixins: [formFullnameMixin],
  props: {
    closeModal: {
      type: Function,
      default: null,
    },
    hkldUserSelectOptions: {
      type: Array,
      default: undefined,
    },
    hkldHotbContacts: {
      type: [Array, String],
      default: undefined,
    },
  },
  data () {
    const date = (new Date()).getTime()
    return {
      regex,
      scheduledTransactionDateError: false,
      scheduledTransactionDate: date,
      hkldContactInfoError: '',
      remarkToTenant: '',
      selectedUser: this.hkldHotbContacts || [{name: '', id: ''}],
      yesterday: new Date((new Date()).getTime() - dateUtil.ONE_DAY_MILLI),
      contactInfo: {
        firstName: {
          error: false,
          value: '',
          mandatory: true,
        },
        lastName: {
          error: false,
          value: '',
          mandatory: true,
        },
        phoneNumber: {
          error: false,
          value: '',
          mandatory: true,
        },
        email: {
          error: false,
          value: '',
          mandatory: true,
        },
        remark: {
          error: false,
          value: '',
        },
        additionalEmails: {
          error: false,
          value: '',
        },

      },
    }
  },
  computed: {
    ...mapGetters('hotb', ['currentHotb', 'isHandover']),
    notSelectUsers () {
      const ret = lodash.filter(this.hkldUserSelectOptions, item => {
        const index = lodash.findIndex(this.selectedUser, user => {
          return user.id === item.id
        })
        return index === -1
      })
      return ret.sort((a, b) => a.name.localeCompare(b.name))
    },

    options () {
      let startTime = '00:00'
      let interval = 15
      if (this.scheduledTransactionDate) {
        let scheduleDate = new Date(this.scheduledTransactionDate)
        scheduleDate.setHours(0, 0, 0)
        if (scheduleDate <= new Date()) {
          const currentTime = moment(new Date(), 'HH:mm')
          let startHour = currentTime.hours()
          let startMin = Math.ceil((currentTime.minutes() / interval)) * interval
          if (startMin === 60) {
            startHour += 1
            startMin = 0
          }
          if (startHour >= 24) {
            startHour = 24
            startMin = 0
          }
          startTime = `${startHour}:${startMin}`
        }
      }
      return dateUtil.generateTimeDurations(interval, startTime)
    },
  },
  watch: {
    hkldHotbContacts (val) {
      if (!this.hkldHotbContacts.length) {
        this.selectedUser = [{name: '', id: ''}]
      } else {
        this.selectedUser = []
      }
      this.hkldHotbContacts.map(item => {
        const contact = {
          name: item.displayName,
          id: item.id,
          email: this.getContactValueByKey(item, 'EMAIL'),
          phone: this.getContactValueByKey(item, 'PHONE_NUMBER'),
        }
        this.selectedUser.push(contact)
      })
      this.remarkToTenant = lodash.get(this.currentHotb, 'remarkToTenant', '')
      this.resetContactInfo()
      const defaultValue = new Date() > new Date(this.currentHotb.transactionDate) ? new Date() : new Date(this.currentHotb.transactionDate)
      const fomattedDefaultValue = dateUtil.formatDate(defaultValue)
      const scheduleDate = lodash.get(this.currentHotb, 'scheduledTransactionDate', '')
      const defaultScheduledValue = new Date() > new Date(scheduleDate) ? new Date() : new Date(scheduleDate)
      this.scheduledTransactionDate = scheduleDate ? defaultScheduledValue.getTime()
        : (new Date(dateUtil.convertArrayToDate(fomattedDefaultValue, this.options[0]))).getTime()
    },
  },
  methods: {
    ...mapMutations('hotb', {
      updateContactInfo: mutationTypes.HOTB_UPDATE_CONTACT_INFO,
      updateFieldByPath: mutationTypes.HOTB_UPDATE_FIELD_PATH,
      updataHkldContactInfo: mutationTypes.HOTB_UPDATE_HKLD_HANDOVER_CONTACTS,
    }),
    ...mapActions('hotb', ['updateHotb', 'patchStatus']),
    updateScheduledTransactionDate (val) {
      if (val) {
        this.scheduledTransactionDateError = false
        this.scheduledTransactionDate = val
      }
    },
    resetContactInfo () {
      if (this.currentHotb) {
        this.currentHotb.contacts && this.currentHotb.contacts.map(item => {
          switch (item.type) {
            case contactTypes.NAME:
              this.contactInfo.firstName.value = item.value.firstName
              this.contactInfo.firstName.error = false

              this.contactInfo.lastName.value = item.value.lastName
              this.contactInfo.lastName.error = false

              break
            case contactTypes.PHONE_NUMBER:
              this.contactInfo.phoneNumber.value = item.value
              this.contactInfo.phoneNumber.error = false

              break
            case contactTypes.EMAIL:
              this.contactInfo.email.value = item.value
              this.contactInfo.email.error = false

              break
            default:
              break
          }
        })
        this.contactInfo.additionalEmails.value = this.currentHotb.additionalEmails
        this.contactInfo.additionalEmails.error = false
      }
    },
    updateRemark (val) {
      this.remarkToTenant = val
    },

    updateItemValue (key, val) {
      if (key === 'email') {
        this.contactInfo[key].error = !regex.email.test(val)
      } else if (key === 'additionalEmails') {
        this.contactInfo[key].error = splitEmails(val).some(mail => mail !== '' && !regex.email.test(mail))
      } else {
        this.contactInfo[key].error = !val
      }
      this.contactInfo[key].value = val
    },

    getHkldUserHklOptions (index) {
      const currentUser = this.selectedUser[index]
      let options = (this.notSelectUsers || []).map(item => ({
        name: item.displayName != null ? item.displayName : item.name,
        value: item.id,
      }))
      if (currentUser && currentUser.id) {
        const alreadyIncluded = options.some(opt => opt.value === currentUser.id)
        if (!alreadyIncluded) {
          options = [{ name: currentUser.name, value: currentUser.id }, ...options]
        }
      }
      return options.sort((a, b) => a.name.localeCompare(b.name))
    },
    updateSelect (userId, index) {
      if (!userId) {
        return
      }
      const isIncluded = lodash.some(this.selectedUser, (u, i) => i !== index && u.id === userId)
      if (isIncluded) {
        return
      }
      const item = lodash.find(this.hkldUserSelectOptions, ['id', userId])
      if (!item) {
        return
      }
      const contact = {
        name: item.displayName != null ? item.displayName : item.name,
        id: item.id,
        email: this.getContactValueByKey(item, 'EMAIL'),
        phone: this.getContactValueByKey(item, 'PHONE_NUMBER'),
      }
      this.selectedUser.splice(index, 1, contact)
    },
    async confirm () {
      let hasError = false
      for (const key in this.contactInfo) {
        const error = this.contactInfo[key].error
        const value = this.contactInfo[key].value
        const isMandatory = this.contactInfo[key].mandatory
        if (error || (isMandatory && !value)) {
          this.contactInfo[key].error = true
          hasError = true
        } else {
          this.contactInfo[key].error = false
        }
      }
      if (!this.scheduledTransactionDate) {
        this.scheduledTransactionDateError = true
        hasError = true
      }
      if (lodash.isEmpty(this.selectedUser)) {
        hasError = true
      } else {
        this.selectedUser.map((user, index) => {
          if (!user.id) {
            hasError = true
            user.emptyUser = true
          }
        })
      }
      if (hasError) {
        this.$forceUpdate()
        return
      }
      const contacts = lodash.cloneDeep(lodash.get(this.currentHotb, 'contacts', []))
      contacts.map(item => {
        switch (item.type) {
          case contactTypes.NAME:
            item.value.firstName = this.contactInfo.firstName.value
            item.value.lastName = this.contactInfo.lastName.value
            break
          case contactTypes.PHONE_NUMBER:
            item.value = this.contactInfo.phoneNumber.value
            break
          case contactTypes.EMAIL:
            item.value = this.contactInfo.email.value
            break
          default:
            break
        }
      })
      const khldUsersIds = []
      this.selectedUser.map(user => {
        delete user.emptyUser
        khldUsersIds.push(user.id)
      })
      const formattedAdditionalEmails = splitEmails(this.contactInfo.additionalEmails.value).join('; ')
      this.updateFieldByPath({ path: ['contacts'], newValue: contacts })
      this.updateFieldByPath({ path: ['hkldContactId'], newValue: khldUsersIds })
      this.updateFieldByPath({ path: ['scheduledTransactionDate'], newValue: this.scheduledTransactionDate })
      this.updateFieldByPath({ path: ['remarkToTenant'], newValue: this.remarkToTenant })
      this.updateFieldByPath({ path: ['additionalEmails'], newValue: formattedAdditionalEmails })

      const hotbType = hotbAttrTypes[this.currentHotb.type]
      await this.updateHotb({ attrs: ARRANGE_ATTRS })
      await this.patchStatus({
        request: { status: hotbStatus.SCHEDULED },
        action: ActionTypes.hotb.schedule,
      })
      sendEmail(EmailTypes[hotbType].arrange)
      this.cancel()
    },
    getRequest () {
      let request = {
        date: this.currentHotb.transactionDate,
      }
      for (const key in this.contactInfo) {
        request[key] = this.contactInfo[key].value
      }
      return request
    },
    cancel () {
      this.closeModal && this.closeModal()
    },
    removeSelectedUser (index) {
      this.selectedUser.splice(index, 1)
    },
    getContactValueByKey (item, key) {
      let value = ''
      if (!item) {
        return value
      }
      if (key === 'EMAIL' && item.email) {
        return item.email
      }
      if (key === 'PHONE_NUMBER' && item.phone) {
        return item.phone
      }
      if (!Array.isArray(item.contacts)) {
        return value
      }
      item.contacts.map(contact => {
        if (key === 'NAME' && contact.type === key) {
          value = this.displayI18nFullName(contact.value.firstName, contact.value.lastName)
        } else if (contact.type === key) {
          value = contact.value
        }
      })
      return value
    },
    addEditabelMultiSelect () {
      this.selectedUser.push({name: '', id: ''})
    },
    deleteSelectedUser (index) {
      this.selectedUser.splice(index, 1)
    },
  },
}
</script>

<style lang='scss'>
.invite-contact-modal {
  .date-time-select {
    .vdp-datepicker {
      min-width: 150px;
    }
  }
  .multiselect__content-wrapper {
    ul {
      background: #fff;
    }
  }
  p.title {
    font-weight: bold;
    font-size: 16px;
  }
  .actual-handover-date-error {
    .vdp-datepicker {
      div.row {
        background: #f9dcda;
      }
    }
    .errorTip {
      left: 30px !important;
    }
  }
  .hkldUserErr {
    input {
      background: #f9dcda;
    }
    .errorTip {
      top: 60px !important;
    }
  }
  .actual-handover-date {
    span.title {
      font-weight: bold;
      height: 20px;
      display: block;
      margin-bottom: 5px;
    }
  }
  .group-btns{
    height: 60px;
    text-align: right;
    div {
      display: inline-block;
      margin-top: 15px;
    }
    .cancel {
      width: 100px;
      margin-right: 15px;
    }
  }
  .hkld-contact {
    .main-contact.info {
      width: 96%;
    }
    .phone-number,
    .email {
    .input-field {
        background: lightgrey;
        height: 40px;
        line-height: 40px;
        padding-left: 10px;
      }
    }
    .seletedUser {
      span {
        height: 30px;
        padding: 5px;
        display: inline-block;
      }
      i {
        cursor: pointer;
        margin-left: 5px;
      }
    }
  }
  .contact-info {
    .title {
      font-weight: bold;
      margin-bottom: 5px;
    }
    .main-contact {
      width: 360px;
      i.fa-plus, i.fa-times {
        cursor: pointer;
        position: absolute;
        top: 13px;
        right: -25px;
      }
      .multi-content {
        position: relative;
        margin-bottom: 10px;
      }
      .multi-content:last-child {
        margin: 0;
      }
    }
    .info {
      margin-top: 10px;
      display: inline-block;
      .title {
        display: block;
      }
    }
    input {
      padding: 0;
      margin: 0;
      width: 100%;
      text-indent: 10px;
      border: none !important;
    }
    .input-field {
      border: solid 1px #B5B5B5;
      .text-input {
        margin: 0;
      }
    }
    .text-input textarea {
      width: 100%
    }
  }
  .main-contact {
    position: relative;
    margin-right: 13px;
    .content {
      position: relative;
      display: flex;
      margin-bottom: 15px;
      i.fa {
        position: absolute;
        cursor: pointer;
        top: 15px;
        right: 0;
      }
    }
    .content:last-child {
      margin-bottom: 0;
    }
    .input-field {
      flex: 1;
      display: inline-block;
      width: 180px;
    }
    .firstNameErr {
      bottom: -30px;
    }
    .lastNameErr {
      left: 175px;
      width: 240px;
      bottom: -30px;
    }
  }
  .phone-number {
    width: 360px;
    position: relative;
  }
  .remark {
    margin-top: 10px;
  }
  .email,
  .remark {
    width: 100%;
    .errorTip {
      left: 30px;
    }
  }
  .error-highlight {
    background-color: #f9dcda;
  }
}
.tooltip.vue-tooltip-theme {
  z-index: 10000;
}
</style>
